// Scroll position across a list → detail → Back round trip (SEM-6350).
//
// Not in the query string, unlike the filter/page half of that round trip
// (use-url-filters.ts): scroll position is a property of a HISTORY ENTRY, not
// of the view the URL names, so two people opening the same shared link should
// both start at the top.
//
// React Router's own <ScrollRestoration/> is unusable here for one reason: it
// drives `window`, and this app's window never scrolls — the scroller is
// AppShell's <main>. Keying by `location.key` is the router's own scheme.
//
// **Only a POP moves the scroller.** <ScrollRestoration/> also resets forward
// navigations to the top; copying that here would be a regression, because
// this app has navigations that do not unmount the list under them — /people
// opens its detail as a drawer OVER the live list, and every filter write is
// a `replace` to a fresh key on a screen that never re-rendered from scratch.
// Both would snap a user reading row 60 back to row 1.
//
// Memory, not sessionStorage: a reload drops the query cache the list renders
// from, so a restored offset would point into a list not yet fetched.

import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import { useLocation, useNavigationType } from "react-router";

const positions = new Map<string, number>();

/** Restores `ref`'s scroll offset when the user returns to a history entry. */
export function useScrollRestoration(ref: RefObject<HTMLElement | null>): void {
  const { key } = useLocation();
  const navigationType = useNavigationType();
  // The save listener is bound ONCE and reads the key from here. Rebinding it
  // per key would leave the old key's listener in place for the restore below,
  // which scrolls — and that entry would then be overwritten with the position
  // of the entry the user just moved to.
  const keyRef = useRef(key);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return;
    keyRef.current = key;
    const saved = positions.get(key);
    if (navigationType !== "POP" || saved === undefined) return;
    el.scrollTop = saved;
  }, [key, navigationType, ref]);

  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const save = (): void => {
      positions.set(keyRef.current, el.scrollTop);
    };
    el.addEventListener("scroll", save, { passive: true });
    return (): void => {
      el.removeEventListener("scroll", save);
    };
  }, [ref]);
}
